import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { SITE } from "../data/site";
import { ArrowUpRight } from "lucide-react";
import LeadModal from "./LeadModal";

const HERO_BG =
  "https://images.unsplash.com/photo-1472803828399-39d4ac53c6e5?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400";

function Counter({ to, suffix = "", trigger }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!trigger) return;
    let start;
    const end = parseInt(to, 10);
    const dur = 1600;
    let raf;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * end));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to, trigger]);
  return (
    <span className="tabular">
      {val}
      {suffix}
    </span>
  );
}

function MaskedLine({ children, delay = 0 }) {
  return (
    <span className="block overflow-hidden" style={{ lineHeight: 1.2, paddingTop: "0.05em", paddingBottom: "0.18em" }}>
      <motion.span
        initial={{ y: "115%", opacity: 0 }}
        animate={{ y: "0%", opacity: 1 }}
        transition={{ delay, duration: 1.05, ease: [0.65, 0, 0.35, 1] }}
        className="block will-change-transform"
        style={{ lineHeight: 1 }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const statsRef = useRef(null);
  const statsInView = useInView(statsRef, { once: true, margin: "-10%" });
  const [leadOpen, setLeadOpen] = useState(false);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const titleY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);
  const dim = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section
      ref={ref}
      id="hero"
      className="relative min-h-[100svh] w-full overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 will-change-transform"
        aria-hidden
      >
        <img
          src={HERO_BG}
          alt=""
          className="w-full h-[120%] object-cover"
          draggable={false}
        />
        <motion.div
          style={{ opacity: dim }}
          className="absolute inset-0 bg-gradient-to-b from-[#0e0e0e]/60 via-[#0e0e0e]/60 to-[#0e0e0e]"
        />
        <div className="absolute inset-0 grain opacity-40" />
      </motion.div>

      {/* Top rail */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 0.8 }}
        className="absolute top-[92px] left-0 right-0 z-10 hidden md:flex items-center justify-end px-10 text-[11px] tracking-[0.3em] uppercase text-[var(--ink-mute)]"
      >
        <span>Vol. XXX · Chapter I</span>
      </motion.div>

      {/* Center title */}
      <motion.div
        style={{ y: titleY }}
        className="relative z-10 min-h-[100svh] flex flex-col justify-center px-6 md:px-10 pt-28 pb-44"
      >
        <div className="max-w-[1600px] mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.9 }}
            className="flex items-center gap-4 text-[11px] tracking-[0.32em] uppercase text-[var(--ink-dim)] mb-8"
          >
            <span className="w-10 h-px bg-[var(--gold)]" />
            <span className="text-[var(--gold)] font-mono">01</span>
            <span>A Consultancy for Brand Builders</span>
          </motion.div>

          <h1
            className="font-display text-[16vw] md:text-[9.5vw] tracking-[-0.025em] text-[var(--ink)]"
            data-testid="hero-headline"
            style={{ lineHeight: 1 }}
          >
            <MaskedLine delay={0.7}>Strategy.</MaskedLine>
            <MaskedLine delay={0.9}>
              <span className="italic text-[var(--gold)] font-light">Scale.</span>
            </MaskedLine>
            <MaskedLine delay={1.1}>Success.</MaskedLine>
          </h1>

          <div className="mt-10 md:mt-14 grid md:grid-cols-12 gap-8 items-end">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.9 }}
              className="md:col-span-6 md:col-start-6 text-[15px] md:text-[17px] leading-[1.55] text-[var(--ink-dim)] max-w-[46ch]"
            >
              Orentis partners with founders and category defining companies,
              turning conviction into positioning, positioning into
              distribution, distribution into legacy.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.65, duration: 0.9 }}
              className="md:col-span-3 md:col-start-1 md:row-start-1 flex flex-col gap-3"
            >
              <a
                href="#services"
                className="group inline-flex items-center gap-3 border border-white/15 hover:border-[var(--gold)] transition-colors duration-500 px-5 py-4 rounded-full text-[12px] tracking-[0.28em] uppercase"
                data-testid="hero-cta-services"
              >
                <span>Our Practices</span>
                <span className="inline-flex w-8 h-8 rounded-full border border-white/15 group-hover:border-[var(--gold)] group-hover:bg-[var(--gold)] group-hover:text-[var(--bg)] transition-all duration-500 items-center justify-center">
                  <ArrowUpRight size={14} />
                </span>
              </a>
              <button
                type="button"
                onClick={() => setLeadOpen(true)}
                className="text-[12px] tracking-[0.28em] uppercase text-[var(--ink-dim)] hover:text-[var(--gold)] transition-colors duration-300 pl-2 text-left"
                data-testid="hero-cta-conversation"
              >
                → Begin a Conversation
              </button>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Stats strip */}
      <div
        ref={statsRef}
        className="absolute bottom-0 left-0 right-0 z-10 border-t border-white/10 bg-[#0e0e0e]/40 backdrop-blur-md"
        data-testid="hero-stats"
      >
        <div className="mx-auto max-w-[1600px] px-6 md:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            {SITE.stats.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                animate={statsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: i * 0.12, duration: 0.7 }}
                className="py-6 md:py-8 px-4 md:px-8 first:pl-0 last:pr-0 flex flex-col gap-1"
              >
                <div className="font-display text-4xl md:text-5xl leading-none">
                  <Counter to={s.value} suffix={s.suffix} trigger={statsInView} />
                </div>
                <div className="text-[10px] md:text-[11px] tracking-[0.28em] uppercase text-[var(--ink-mute)]">
                  {s.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 0.8 }}
        className="absolute bottom-[135px] md:bottom-[155px] right-6 md:right-10 z-10 hidden md:flex flex-col items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-[var(--ink-mute)]"
      >
        <span>Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          className="w-px h-12 bg-gradient-to-b from-[var(--gold)] to-transparent"
        />
      </motion.div>

      {/* Lead modal */}
      <LeadModal open={leadOpen} onOpenChange={setLeadOpen} />
    </section>
  );
}
